import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GENRES, REGIONS, YEARS, movies, MOVIE_DISCLAIMER } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageContainer from "@/components/base/PageContainer";
import FilterGroup, { type FilterOption } from "@/pages/category/components/FilterGroup";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 12;

const toOptions = (list: string[]): FilterOption[] => [
  { value: "all", label: "全部" },
  ...list.map((item) => ({ value: item, label: item })),
];

const TYPE_OPTIONS: FilterOption[] = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
];

const SORT_OPTIONS: FilterOption[] = [
  { value: "latest", label: "最新" },
  { value: "hot", label: "热门" },
  { value: "rating", label: "评分" },
];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const type = searchParams.get("type") || "all";
  const genre = searchParams.get("genre") || "all";
  const region = searchParams.get("region") || "all";
  const year = searchParams.get("year") || "all";
  const sort = searchParams.get("sort") || "latest";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

  const categoryName = useMemo(() => {
    if (region !== "all") {
      const suffix = type === "tv" ? "电视剧" : type === "movie" ? "电影" : "影视";
      return `${region}${suffix}`;
    }
    if (type === "movie") return "电影";
    if (type === "tv") return "电视剧";
    if (genre !== "all") return `${genre}影视`;
    return "全部影视";
  }, [type, region, genre]);

  usePageMeta(
    `影视影院 - ${categoryName} - 影视资源大全`,
    `影视影院「${categoryName}」分类汇集丰富影视资源，可按类型、地区、年份筛选，并支持最新、热门、评分排序，帮你快速找到想看的内容。`,
    `影视影院,${categoryName},影视资源,电影,电视剧,影视分类`
  );

  const { list, totalPages, total } = useMemo(() => {
    let result = [...movies];
    if (type !== "all") result = result.filter((m) => m.type === type);
    if (genre !== "all") result = result.filter((m) => m.genres.includes(genre));
    if (region !== "all") result = result.filter((m) => m.region === region);
    if (year !== "all") result = result.filter((m) => String(m.year) === year);

    result.sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "hot") {
        const popA = a.rating * 3 + (a.year - 2020);
        const popB = b.rating * 3 + (b.year - 2020);
        return popB - popA;
      }
      return b.releaseDate.localeCompare(a.releaseDate);
    });

    const pages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
    const start = (page - 1) * PAGE_SIZE;
    return { list: result.slice(start, start + PAGE_SIZE), totalPages: pages, total: result.length };
  }, [type, genre, region, year, sort, page]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const updateFilter = (key: string, value: string, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete(key);
    else next.set(key, value);
    if (resetPage) next.delete("page");
    setSearchParams(next);
  };

  const resetFilters = () => setSearchParams(new URLSearchParams());

  const hasActiveFilter =
    type !== "all" || genre !== "all" || region !== "all" || year !== "all" || sort !== "latest";

  const filters = (
    <>
      <FilterGroup label="分类" options={TYPE_OPTIONS} value={type} onSelect={(v) => updateFilter("type", v)} />
      <FilterGroup label="类型" options={toOptions(GENRES)} value={genre} onSelect={(v) => updateFilter("genre", v)} />
      <FilterGroup label="地区" options={toOptions(REGIONS)} value={region} onSelect={(v) => updateFilter("region", v)} />
      <FilterGroup label="年份" options={toOptions(YEARS)} value={year} onSelect={(v) => updateFilter("year", v)} />
      <FilterGroup label="排序" options={SORT_OPTIONS} value={sort} onSelect={(v) => updateFilter("sort", v)} />
    </>
  );

  return (
    <div className="w-full bg-background-100">
      <PageContainer className="py-6 md:py-8">
        <Breadcrumb items={[{ label: "影视分类", to: "/category" }, { label: categoryName }]} />

        <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
              {categoryName}
            </h1>
            <p className="mt-2 text-sm text-foreground-500 max-w-2xl">
              本章节收录「{categoryName}」相关内容，支持按类型、地区、年份多维度筛选，并提供最新、热门、评分三种排序方式。
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground-500 whitespace-nowrap">
              共 <strong className="text-primary-700 font-label">{total}</strong> 部作品
            </span>
            <button
              type="button"
              onClick={() => setFilterOpen(true)}
              className="lg:hidden inline-flex items-center gap-1 px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-label whitespace-nowrap cursor-pointer"
            >
              <i className="ri-filter-3-line" />
              筛选
            </button>
          </div>
        </div>

        {/* Desktop filters */}
        <div className="hidden lg:block mt-6 rounded-lg bg-background-50 border border-background-200 px-4 divide-y divide-background-200">
          {filters}
        </div>

        {/* Results */}
        <div className="mt-6">
          {list.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {list.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <div className="mt-8">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onChange={(next) => updateFilter("page", String(next), false)}
                />
              </div>
            </>
          ) : (
            <div className="py-16 flex flex-col items-center text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                <i className="ri-inbox-archive-line text-3xl" />
              </span>
              <p className="text-lg font-heading font-bold text-foreground-800">暂无符合条件的影视作品</p>
              <p className="mt-2 text-sm text-foreground-500">试试更换筛选条件，或重置全部筛选重新浏览。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-label transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          )}
        </div>

        {hasActiveFilter && list.length > 0 && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}

        <p className="mt-8 text-xs text-foreground-400 text-center leading-relaxed">{MOVIE_DISCLAIMER}</p>
      </PageContainer>

      {/* Mobile fullscreen filter */}
      {filterOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] bg-background-50 flex flex-col animate-fade-in">
          <div className="flex items-center justify-between px-4 h-14 border-b border-background-200 shrink-0">
            <span className="font-heading font-bold text-base text-foreground-950">筛选条件</span>
            <button
              type="button"
              aria-label="关闭筛选"
              onClick={() => setFilterOpen(false)}
              className="w-10 h-10 flex items-center justify-center text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-line text-2xl" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 divide-y divide-background-200">
            {filters}
          </div>
          <div className="shrink-0 p-4 border-t border-background-200 flex gap-3">
            <button
              type="button"
              onClick={resetFilters}
              className="flex-1 py-3 rounded-md border border-background-300 text-sm font-label text-foreground-700 whitespace-nowrap cursor-pointer"
            >
              重置筛选
            </button>
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="flex-1 py-3 rounded-md bg-primary-600 text-white text-sm font-label whitespace-nowrap cursor-pointer"
            >
              查看结果（{total}）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}